import { AsYouType, isValidPhoneNumber, parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";

// cf libphonenumber-js types
const COUNTRY_CODES = [
  // Most common country codes for us
  "FR",
  "MA", // Morocco
  "TN", // Tunisia
  "DZ", // Algeria

  // Other country codes
  "AC",
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TA",
  "TC",
  "TD",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "XK",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
] as const;

/**
 * Permet de valider un numéro de téléphone, fixe ou mobile
 */
export const validatePhoneNumber = (phoneNumber?: string): boolean => {
  if (!phoneNumber) return false;

  // S'il a une extension internationale valide, ça devrait passer ici
  if (isValidPhoneNumber(phoneNumber)) return true;

  // On teste tous les pays si le numéro est valide pour l'un d'entre eux au format national
  for (const countryCode of COUNTRY_CODES) {
    if (isValidPhoneNumber(phoneNumber, countryCode)) return true;
  }

  return false;
};

export const formatPhoneNumberSimple = function (phoneNumber: string) {
  const defaultCountry: CountryCode = "FR";
  const parsedPhoneNumber = parsePhoneNumberWithError(phoneNumber, defaultCountry);
  return parsedPhoneNumber.number;
};

export const formatPhoneNumber = (phoneNumber: string | null | undefined): string | undefined => {
  if (!phoneNumber) return undefined;

  if (phoneNumber.startsWith("+330")) phoneNumber = phoneNumber.replace("+330", "+33");
  if (!validatePhoneNumber(phoneNumber)) throw new Error("Invalid phone number: " + phoneNumber);

  try {
    const parsedPhoneNumber = parsePhoneNumberWithError(phoneNumber, { extract: false });
    if (parsedPhoneNumber.isValid()) return parsedPhoneNumber.format("E.164");
  } catch {
    // Parsing error but we continue with specific country codes
  }

  for (const countryCode of COUNTRY_CODES) {
    try {
      const parsedPhoneNumber = parsePhoneNumberWithError(phoneNumber, {
        extract: false,
        defaultCountry: countryCode,
      });
      if (parsedPhoneNumber.isValid()) return parsedPhoneNumber.format("E.164");
    } catch {
      // Parsing error but we continue with the next country code
    }
  }

  throw new Error("Invalid phone number");
};

/**
 * Non-throwing counterpart of formatPhoneNumber. Returns undefined instead of throwing when the
 * number cannot be parsed, for the flows where an unusable number should be treated as absent
 * rather than fail the whole operation (job board payloads, call logs, etc).
 */
export const tryFormatPhoneNumber = (phoneNumber: string | null | undefined): string | undefined => {
  try {
    return formatPhoneNumber(phoneNumber);
  } catch {
    return undefined;
  }
};

// The canonical French example numbers, which candidates type in when a form makes the phone field
// mandatory. Compared against E.164, so we normalise first: "+33 6 12 34 56 78" is the same number.
const PLACEHOLDER_PHONE_NUMBERS = ["+33612345678", "+33123456789"];

export const isPlaceholderPhoneNumber = (phoneNumber?: string | null): boolean => {
  const formattedPhoneNumber = tryFormatPhoneNumber(phoneNumber);
  if (!formattedPhoneNumber) return false;

  return PLACEHOLDER_PHONE_NUMBERS.includes(formattedPhoneNumber);
};

export const formatPartialPhoneNumber = (phoneNumber: string): string => {
  try {
    return new AsYouType().input(phoneNumber);
  } catch {
    // We haven't found the country code so we continue with specific country codes
  }

  for (const countryCode of COUNTRY_CODES) {
    try {
      return new AsYouType(countryCode).input(phoneNumber);
    } catch {
      // Parsing error but we continue with the next country code
    }
  }

  // Default value
  return phoneNumber;
};

export const isPhoneNumberValid = function (phoneNumber?: string, countryCode?: CountryCode) {
  if (!phoneNumber) return false;

  return isValidPhoneNumber(phoneNumber, countryCode);
};

export const stylifyPhoneNumber = (phoneNumber: string | null): string => {
  if (!phoneNumber) return "";

  // Remove all spaces and dashes for normalization
  const raw = phoneNumber.replace(/[\s-]/g, "");

  // French numbers: +33XXXXXXXXX or 0XXXXXXXXX
  const frMatch = raw.match(/^(?:\+33|0)([1-9])(\d{2})(\d{2})(\d{2})(\d{2})$/);
  if (frMatch) {
    // If +33, keep it, else use 0
    const prefix = raw.startsWith("+33") ? "+33 " : "0";
    return `${prefix}${frMatch[1]} ${frMatch[2]} ${frMatch[3]} ${frMatch[4]} ${frMatch[5]}`;
  }

  // French numbers with spaces already formatted
  const frAlreadyFormatted = phoneNumber.match(/^(?:\+33|0)\s?[1-9](?:\s?\d{2}){4}$/);
  if (frAlreadyFormatted) {
    return phoneNumber;
  }

  // Optionally: handle other country codes (e.g., Morocco +212)
  // For now, just return the input for non-French numbers
  return phoneNumber;
};
